// One dataset's screen: what it holds (its fields and their figures, counts)
// and its latest rows, all from the screen's copy (one sync; no other read).
// How the numbers move over time is for a document's UI (`useData`), drawn
// the way the reader wants; here are the figures of what is there.
// Those who may write it also add, edit and delete rows by hand and manage
// its write tokens.
//
// Promise:
// - Opening it costs the copy's sync (shared with any UI reading the same
//   dataset) and, for a writer, the list of tokens. Rows written elsewhere
//   show while it is open (the copy follows the notices).
// - Without a schema the fields are guessed from the rows (and said so).
// - The rows are newest write first; ROWS_STEP are drawn, and ROWS_STEP more
//   each time the table's end comes into view (from the copy: the screen
//   never draws all 10,000 at once, and asks the server for nothing).
// - A number field opens to the distribution of its values.
// - Whether one may write is the host's to say (`writable`): a reader sees no
//   form, no row actions and no tokens (and they are not asked for).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type DatasetDTO,
  type DatasetField,
  type DatasetRow,
  inferSchema,
} from "@trameria/core/datasets-shared";
import { m } from "@trameria/i18n";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../app/context";
import { ApiError } from "../data/api";
import { apiErrorMessage } from "../data/errors";
import { useFormat } from "../me/profile";
import { DetailHeader } from "../shell/list-detail";
import { useShellHeader } from "../shell/outside/outside";
import { useSideView } from "../shell/side-view";
import { ErrorLine, Skeleton } from "../ui/bits";
import { Button, SMALL_ICON_BUTTON } from "../ui/button";
import { confirmAction } from "../ui/confirm";
import { Caret, Icon } from "../ui/icon";
import { MenuButton } from "../ui/menu";
import { datasetCopies } from "./copies";
import { datasetSyncQuery, useWriteDatasetRows } from "./queries";
import { DatasetRowForm } from "./row-form";
import { type FieldStats, fieldStats, histogram } from "./stats";
import { DatasetTokens } from "./tokens";

/** Rows drawn at first, and added each time the table's end comes into view. */
export const ROWS_STEP = 50;

/**
 * Calls `onView` when it comes into view (the end of the rows' table): the
 * next rows are drawn from the copy, nothing is asked of the server. Where
 * there is no IntersectionObserver, nothing more is drawn.
 */
function MoreOnView({ onView }: { onView(): void }) {
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef(onView);
  latest.current = onView;
  // frontend-development: not a fetch; draws more of the rows already held.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) latest.current();
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} aria-hidden className="h-px" />;
}

const typeLabel = (type: DatasetField["type"]): string =>
  ({
    number: m.datasets_type_number(),
    string: m.datasets_type_string(),
    date: m.datasets_type_date(),
    datetime: m.datasets_type_datetime(),
    boolean: m.datasets_type_boolean(),
    json: m.datasets_type_json(),
  })[type];

/** Bytes as people read them (KB, MB). */
export function sizeText(bytes: number): string {
  const f = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${f.format(bytes / 1000)} KB`;
  return `${f.format(bytes / 1_000_000)} MB`;
}

function Cell({ value }: { value: unknown }) {
  if (value === null || value === undefined)
    return <span className="text-placeholder">—</span>;
  if (typeof value === "boolean") return <>{value ? "✓" : ""}</>;
  if (typeof value === "number")
    return (
      <span className="tabular-nums">
        {new Intl.NumberFormat(undefined, {
          maximumFractionDigits: 6,
        }).format(value)}
      </span>
    );
  if (typeof value === "object")
    return <span className="font-mono">{JSON.stringify(value)}</span>;
  return <>{String(value)}</>;
}

function Section({
  title,
  note,
  end,
  children,
}: {
  title: string;
  note?: ReactNode;
  end?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mt-8">
      <div className="flex min-h-8 items-center gap-2">
        <h2 className="min-w-0 flex-1 text-[13px] text-fg">{title}</h2>
        {end}
      </div>
      {note && (
        <p className="mt-0.5 text-[11.5px] leading-[1.6] text-faint">{note}</p>
      )}
      {children}
    </section>
  );
}

const numberText = (n: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);

/** What a field holds: its type's figures, each one piece. */
function statsParts(field: DatasetField, s: FieldStats | undefined): string[] {
  if (!s || s.filled === 0) return [];
  const unit = field.unit ? ` ${field.unit}` : "";
  if (s.min !== undefined && s.max !== undefined)
    return [
      m.datasets_stat_min({ value: numberText(s.min) + unit }),
      m.datasets_stat_max({ value: numberText(s.max) + unit }),
      ...(s.mean !== undefined
        ? [m.datasets_stat_mean({ value: numberText(s.mean) + unit })]
        : []),
      ...(s.sum !== undefined
        ? [m.datasets_stat_sum({ value: numberText(s.sum) + unit })]
        : []),
    ];
  if (s.first !== undefined && s.last !== undefined)
    return s.first === s.last
      ? [s.first]
      : [m.datasets_stat_range({ first: s.first, last: s.last })];
  if (s.distinct !== undefined)
    return [m.datasets_stat_distinct({ count: s.distinct })];
  if (s.on !== undefined) return [m.datasets_stat_on({ count: s.on })];
  return [];
}

const HIST_W = 600;
const HIST_H = 120;

/**
 * How a number field's values spread (a histogram of the copy's rows): equal
 * bins from the least to the greatest, the count of each as a bar. Stretched
 * to the row's width; the labels stay text.
 */
function Distribution({
  rows,
  field,
}: {
  rows: ReadonlyMap<string, DatasetRow>;
  field: DatasetField;
}) {
  const hist = useMemo(
    () => histogram(rows.values(), field.name),
    [rows, field.name],
  );
  if (!hist) return null;
  const top = Math.max(...hist.counts, 1);
  const bar = HIST_W / hist.counts.length;
  const unit = field.unit ? ` ${field.unit}` : "";
  return (
    <figure
      role="img"
      aria-label={m.datasets_distribution({ field: field.name })}
      className="m-0 pt-1"
    >
      <div className="flex items-baseline justify-between text-[11px] text-faint">
        <span>{m.datasets_distribution_title()}</span>
        <span className="tabular-nums">
          {m.datasets_distribution_top({ count: numberText(top) })}
        </span>
      </div>
      <svg
        width="100%"
        height={HIST_H}
        viewBox={`0 0 ${HIST_W} ${HIST_H}`}
        preserveAspectRatio="none"
        className="mt-1 block text-accent"
        aria-hidden
      >
        <line
          x1={0}
          x2={HIST_W}
          y1={HIST_H - 0.5}
          y2={HIST_H - 0.5}
          stroke="var(--color-border)"
          vectorEffect="non-scaling-stroke"
        />
        {hist.counts.map((c, i) => {
          const h = (c / top) * (HIST_H - 4);
          return (
            <rect
              // biome-ignore lint/suspicious/noArrayIndexKey: the bins are positions
              key={i}
              x={i * bar + bar * 0.08}
              width={bar * 0.84}
              y={HIST_H - h}
              height={h}
              rx={2}
              fill="currentColor"
              opacity={0.85}
            />
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[11px] text-faint tabular-nums">
        <span>{numberText(hist.min) + unit}</span>
        <span>{numberText(hist.max) + unit}</span>
      </div>
    </figure>
  );
}

/**
 * The fields and what each holds: its type, how many rows have it, and its
 * figures (a number's least, greatest, mean and total; a date's range; how
 * many different texts).
 */
function Fields({
  fields,
  keyFields,
  guessed,
  rows,
}: {
  fields: readonly DatasetField[];
  keyFields: readonly string[];
  guessed: boolean;
  rows: ReadonlyMap<string, DatasetRow>;
}) {
  const stats = useMemo(
    () => fieldStats(rows.values(), fields),
    [rows, fields],
  );
  // The number fields opened to their distribution (this screen's own state).
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (name: string) =>
    setOpen((old) => {
      const next = new Set(old);
      if (!next.delete(name)) next.add(name);
      return next;
    });
  const th =
    "border-b border-border-soft px-2.5 py-1.5 text-left font-normal text-[11.5px] text-faint whitespace-nowrap";
  const td =
    "border-b border-border-soft px-2.5 py-2 align-top text-[12.5px] text-body";
  return (
    <Section
      title={m.datasets_fields()}
      note={guessed && fields.length > 0 ? m.datasets_fields_guessed() : null}
    >
      {fields.length === 0 ? (
        <p className="mt-2 text-[12px] text-faint">
          {m.datasets_fields_none()}
        </p>
      ) : (
        <div className="mt-2 overflow-x-auto rounded-[10px] border border-panel-border">
          <table
            aria-label={m.datasets_fields()}
            className="w-full border-collapse [&_tr:last-child>td]:border-b-0"
          >
            <thead>
              <tr>
                <th scope="col" className={th}>
                  {m.datasets_col_field()}
                </th>
                <th scope="col" className={`${th} text-right!`}>
                  {m.datasets_col_filled()}
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => {
                const s = stats.get(f.name);
                const parts = statsParts(f, s);
                const opens = f.type === "number" && (s?.filled ?? 0) > 0;
                const shown = open.has(f.name);
                const name = (
                  <span className="font-mono text-fg">{f.name}</span>
                );
                return (
                  <Fragment key={f.name}>
                    <tr>
                      {/* The name and type, and its figures under them (a
                        phone's width keeps them readable). */}
                      <td className={`${td} ${shown ? "border-b-0" : ""}`}>
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          {opens ? (
                            <button
                              type="button"
                              aria-expanded={shown}
                              onClick={() => toggle(f.name)}
                              className="-ml-1 inline-flex cursor-pointer items-baseline gap-1 rounded-[5px] px-1 hover:bg-hover"
                            >
                              <span className="text-[10px] text-faint">
                                <Caret open={shown} />
                              </span>
                              {name}
                            </button>
                          ) : (
                            name
                          )}
                          {keyFields.includes(f.name) && (
                            <span className="rounded-[4px] bg-hover px-1 text-[10.5px] text-muted">
                              {m.datasets_key()}
                            </span>
                          )}
                          <span className="text-[12px] text-faint">
                            {typeLabel(f.type)}
                            {f.unit && ` ${f.unit}`}
                          </span>
                        </div>
                        {parts.length > 0 && (
                          // Each figure breaks as a whole between figures.
                          <div className="mt-0.5 text-[12px] text-muted tabular-nums">
                            {parts.map((p, i) => (
                              <Fragment key={p}>
                                {i > 0 && " · "}
                                <span className="whitespace-nowrap">{p}</span>
                              </Fragment>
                            ))}
                          </div>
                        )}
                      </td>
                      <td
                        className={`${td} w-24 whitespace-nowrap text-right text-muted tabular-nums ${shown ? "border-b-0" : ""}`}
                      >
                        {numberText(s?.filled ?? 0)}
                      </td>
                    </tr>
                    {shown && (
                      <tr>
                        <td colSpan={2} className={`${td} pt-0 pb-3`}>
                          <Distribution rows={rows} field={f} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function LatestRows({
  datasetId,
  rows,
  fields,
  writable,
  onEdit,
}: {
  datasetId: string;
  rows: readonly DatasetRow[];
  fields: readonly DatasetField[];
  writable: boolean;
  onEdit(row: DatasetRow): void;
}) {
  const format = useFormat();
  const write = useWriteDatasetRows(datasetId);
  const remove = async (row: DatasetRow) => {
    const ok = await confirmAction({
      title: m.datasets_delete_row(),
      body: m.datasets_delete_row_body(),
      action: m.common_delete(),
      danger: true,
    });
    if (ok) write.mutate({ ops: [{ op: "remove", id: row._id }] });
  };
  const th =
    "border-b border-border-soft px-2.5 py-1.5 text-left font-normal text-[11.5px] text-faint whitespace-nowrap";
  const td =
    "border-b border-border-soft px-2.5 py-1.5 align-top text-[12.5px] text-body whitespace-nowrap max-w-[280px] truncate";
  return (
    <div className="mt-2 overflow-x-auto rounded-[10px] border border-panel-border">
      <table
        aria-label={m.datasets_latest()}
        className="w-full border-collapse [&_tr:last-child>td]:border-b-0"
      >
        <thead>
          <tr>
            {fields.map((f) => (
              <th
                key={f.name}
                scope="col"
                // Numbers line up on the right, their heading with them.
                className={`${th} font-mono ${f.type === "number" ? "text-right!" : ""}`}
              >
                {f.name}
              </th>
            ))}
            <th scope="col" className={th}>
              {m.datasets_written_at()}
            </th>
            {writable && <th className={`${th} w-8`} aria-hidden />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id} className="hover:bg-hover">
              {fields.map((f) => (
                <td
                  key={f.name}
                  className={`${td} ${f.type === "number" ? "text-right" : ""}`}
                >
                  <Cell value={row[f.name]} />
                </td>
              ))}
              <td className={`${td} text-faint tabular-nums`}>
                {format.dateTime(row._at)}
              </td>
              {writable && (
                <td className={`${td} py-0.5 pr-1.5`}>
                  <MenuButton
                    label={m.datasets_row_actions()}
                    align="end"
                    className={`${SMALL_ICON_BUTTON} text-faint`}
                    items={[
                      {
                        id: "edit",
                        label: m.datasets_edit_row(),
                        onSelect: () => onEdit(row),
                      },
                      {
                        id: "delete",
                        label: m.datasets_delete_row(),
                        danger: true,
                        onSelect: () => void remove(row),
                      },
                    ]}
                  >
                    <Icon kind="more" size={14} />
                  </MenuButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A dataset's copy (the screen's and any UI's reading it: one sync). */
export function useDatasetCopy(id: string) {
  const { api } = useMeApp();
  const qc = useQueryClient();
  const deps = useMemo(() => ({ api, qc, copies: datasetCopies() }), [api, qc]);
  return useQuery(datasetSyncQuery(deps, id));
}

/** Why a dataset's copy did not come. */
export function datasetLoadError(error: unknown): string {
  return error instanceof ApiError && error.status === 404
    ? m.datasets_not_found()
    : apiErrorMessage(error, m.datasets_load_failed());
}

/**
 * What a dataset holds, drawn from its copy: its facts, fields and rows, and
 * for a writer the row form, the rows' actions and the tokens. `titled`: its
 * title as the page's heading (a viewer names it in its own bar instead).
 */
export function DatasetContent({
  id,
  dto,
  rows,
  writable,
  titled = true,
}: {
  id: string;
  dto: DatasetDTO;
  rows: ReadonlyMap<string, DatasetRow>;
  writable: boolean;
  titled?: boolean;
}) {
  const format = useFormat();
  // A Vault takes no write tokens: the screen says how rows come in instead.
  const writeTokens = useMeApp().extensions.datasets?.writeTokens !== false;
  // The row form: closed, adding one (null) or editing a row.
  const [form, setForm] = useState<{ editing: DatasetRow | null } | null>(null);
  const guessed = !dto.schema;
  const fields = useMemo(
    () => dto.schema?.fields ?? inferSchema(rows.values()).fields,
    [dto.schema, rows],
  );
  // Every row, newest write first (once per copy), and as many of them as
  // are drawn: ROWS_STEP more each time the table's end comes into view.
  const newestFirst = useMemo(() => [...rows.values()].reverse(), [rows]);
  const [shown, setShown] = useState(ROWS_STEP);
  const latest = useMemo(
    () => newestFirst.slice(0, shown),
    [newestFirst, shown],
  );
  const facts = [
    dto.place.kind === "space" ? dto.place.spaceName : null,
    m.datasets_rows({ count: rows.size }),
    sizeText(dto.byteSize),
    dto.retentionDays !== null
      ? m.datasets_retention({ days: dto.retentionDays })
      : null,
    m.datasets_updated({ time: format.dateTime(dto.updatedAt) }),
  ].filter(Boolean);

  return (
    <>
      {titled && (
        <h1 className="mb-1.5 text-[22px] font-normal tracking-[-0.01em] text-fg">
          {dto.title}
        </h1>
      )}
      <p className="text-[12px] text-faint">{facts.join(" · ")}</p>

      <Fields
        fields={fields}
        keyFields={dto.schema?.key ?? []}
        guessed={guessed}
        rows={rows}
      />

      <Section
        title={m.datasets_latest()}
        note={
          rows.size > ROWS_STEP
            ? m.datasets_latest_note({
                shown: Math.min(shown, rows.size),
                count: rows.size,
              })
            : null
        }
        end={
          writable &&
          form === null && (
            <Button onClick={() => setForm({ editing: null })}>
              {m.datasets_add_row()}
            </Button>
          )
        }
      >
        {form !== null && (
          <DatasetRowForm
            // A new form for each row (its values start from the row).
            key={form.editing?._id ?? "new"}
            datasetId={id}
            schema={dto.schema}
            fields={fields}
            timeZone={dto.timezone}
            editing={form.editing}
            onDone={() => setForm(null)}
          />
        )}
        {latest.length === 0 ? (
          <p className="mt-2 text-[12px] text-faint">{m.datasets_no_rows()}</p>
        ) : (
          <>
            <LatestRows
              datasetId={id}
              rows={latest}
              fields={fields}
              writable={writable}
              onEdit={(row) => setForm({ editing: row })}
            />
            {shown < newestFirst.length && (
              <MoreOnView onView={() => setShown((n) => n + ROWS_STEP)} />
            )}
          </>
        )}
      </Section>

      {writable &&
        (writeTokens ? (
          <DatasetTokens datasetId={id} left={dto.tokensLeft} />
        ) : (
          <Section title={m.datasets_tokens()} note={m.datasets_tokens_vault()}>
            {null}
          </Section>
        ))}
    </>
  );
}

export function DatasetScreen({
  id,
  writable,
  headerStart,
}: {
  id: string;
  /**
   * The viewer may write it (the host knows the place's permissions), or how
   * to tell from the dataset.
   */
  writable: boolean | ((dataset: DatasetDTO) => boolean);
  /** Beside the title in the header (the host's: where it lives). */
  headerStart?: ReactNode;
}) {
  const copy = useDatasetCopy(id);
  const dto = copy.data?.dto ?? null;
  const rows = copy.data?.rows;
  // The header as data too: an app draws its title and way back from it.
  const header = useMemo(
    () => ({ title: dto?.title ?? "", back: true }),
    [dto?.title],
  );
  const side = useSideView() !== null;
  useShellHeader(side ? null : header);

  if (copy.isPending)
    return (
      <>
        <DetailHeader title="" />
        <div className="px-6 pt-7">
          <Skeleton lines={[40, 70, 55]} />
        </div>
      </>
    );
  if (copy.isError || !dto || !rows)
    return (
      <>
        <DetailHeader title="" />
        <div className="px-6 pt-7">
          <ErrorLine>{datasetLoadError(copy.error)}</ErrorLine>
        </div>
      </>
    );

  return (
    <>
      <DetailHeader header={header} start={headerStart} />
      <div className="panel-scroll min-h-0 flex-1 select-text overflow-y-auto">
        <div className="mx-auto max-w-[920px] px-6 pt-7 pb-16">
          <DatasetContent
            id={id}
            dto={dto}
            rows={rows}
            writable={typeof writable === "function" ? writable(dto) : writable}
          />
        </div>
      </div>
    </>
  );
}
